import { FontAwesome } from '@expo/vector-icons';
import { debounce } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Control, Controller, FieldValues, Path, UseFormClearErrors, UseFormSetError } from 'react-hook-form';
import { StyleSheet, TextInputProps, View } from 'react-native';

import { useValidateAchCheck } from '@/api/hooks/paymentAccountsHooks';
import { i18n } from '@/utils/i18n';

import DotsLoader from '../DotsLoader';
import Text from '../Text';
import TextInputField from './TextInputField';

export type BankRoutingNumberInputProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  setError: UseFormSetError<T>;
  clearErrors: UseFormClearErrors<T>;
  onSubmitEditing?: TextInputProps['onSubmitEditing'];
  returnKeyLabel?: TextInputProps['returnKeyLabel'];
  returnKeyType?: TextInputProps['returnKeyType'];
};

const BankRoutingNumberInput = <T extends FieldValues>({
  control,
  name,
  setError,
  clearErrors,
  onSubmitEditing,
  returnKeyLabel,
  returnKeyType
}: BankRoutingNumberInputProps<T>) => {
  const [routingNumber, setRoutingNumber] = useState<string | undefined>();
  const [bankName, setBankName] = useState<string | undefined>();

  const { mutateAsync: validateAchCheck, isPending: validatingAchCheck } = useValidateAchCheck();

  const onChangeText = async () => {
    if (control.getFieldState(name).isValidating) return;
    if (control.getFieldState(name).invalid) return;
    if (routingNumber === undefined) return;
    if (routingNumber === '') return;
    validateAchCheck(
      { routingNumber },
      {
        onSuccess: (result) => {
          clearErrors(name);
          setBankName(result.aba.bank_name);
        },
        onError: (e) => {
          setError(name, {
            message: i18n.t('payment_methods.ach_routing_number_invalid')
          });
          setBankName(undefined);
        }
      }
    );
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounced = useMemo(() => debounce(() => onChangeText(), 400), [routingNumber]);

  useEffect(() => {
    debounced();
    return () => {
      debounced.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routingNumber]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange }, fieldState: { error } }) => {
        return (
          <>
            <TextInputField
              name={name}
              control={control}
              onChangeCallback={(input) => {
                if (bankName) setBankName(undefined);
                setRoutingNumber(input);
                onChange(input);
              }}
              label={i18n.t('npoPaymentOnboarding.bank_account_form.bankRoutingNumber_label')}
              placeholder={i18n.t('npoPaymentOnboarding.bank_account_form.bankRoutingNumber_placeholder')}
              returnKeyLabel={returnKeyLabel}
              returnKeyType={returnKeyType}
              type="numeric-string"
              onSubmitEditing={onSubmitEditing}
            />
            {!bankName && validatingAchCheck && !error && (
              <View style={styles.validatingContainer}>
                <Text style={styles.validatingText}>{i18n.t('mix.validating')}</Text>
                <DotsLoader size={3} color="gray" />
              </View>
            )}
            {bankName && !error && !validatingAchCheck && (
              <View style={styles.bankInfoContainer}>
                <FontAwesome name="bank" size={18} color="black" />
                <Text style={styles.bankInfoText}>{bankName}</Text>
              </View>
            )}
          </>
        );
      }}
    />
  );
};

export default BankRoutingNumberInput;

const styles = StyleSheet.create({
  formContainer: {
    flex: 1,
    width: '100%',
    justifyContent: 'space-between'
  },

  inputsContainer: {
    flex: 1,
    width: '100%',
    gap: 15
  },

  title: {
    fontSize: 22,
    color: 'black',
    alignSelf: 'center'
  },

  options: {
    flexDirection: 'row',
    width: '100%',
    gap: 10,
    flex: 1,
    justifyContent: 'center'
  },

  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  },

  validatingContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 2
  },

  validatingText: {
    color: 'gray',
    fontSize: 13
  },

  bankInfoContainer: {
    padding: 10,
    flexDirection: 'row',
    gap: 5,
    alignItems: 'center',
    borderRadius: 10,
    backgroundColor: 'lightgray'
  },

  bankInfoText: {
    flex: 1,
    fontSize: 16,
    color: 'black'
  }
});
